import React, { ReactNode } from "react";
import GetText from "../../i18n/GetText";
import LinkOverlay from "../LinkOverlay/LinkOverlay";
import MaybeLink from "../MaybeLink/MaybeLink";
import WithSpacing from "../Spacing/Spacing";
import TagInfo from "../TagInfo/TagInfo";
import Text, { TextVariant } from "../Text/Text";
import Truncate from "../Truncate/Truncate";
import styles from "./CardContent.module.scss";

interface CardContentProps {
  title?: string;
  titleBefore?: ReactNode;
  tags?: ReactNode;
  titleVariant?: TextVariant;
  text?: ReactNode;
  textVariant?: TextVariant;
  /**
   * 2 by default, -1 for unlimited.
   */
  textMaxLines?: number;
  href?: string;
  readMore?: boolean;
  children?: ReactNode;
  titleAs?: string;
}

// We go to some effort so we can be used stretched in a flex row
// and have the whitespace in stretched cards be between header
// and body. This helps us cope with variable title lengths.
//
// Note: Must be used inside Card or some other position: relative
// container as it will overlay the link.
const CardContent = ({
  titleBefore,
  title,
  titleAs,
  titleVariant = "h4",
  tags,
  text,
  textVariant = "defaultLight",
  textMaxLines = 2,
  href,
  readMore,
  children
}: CardContentProps) => (
  <div className={styles.root}>
    {(title || tags) && (
      <WithSpacing>
        <div className={styles.heading}>
          {titleBefore}
          {title && (
            <Text variant={titleVariant} as={titleAs ?? titleVariant}>
              <MaybeLink to={href} as={LinkOverlay} className={styles.title}>
                {title}
              </MaybeLink>
            </Text>
          )}
          {typeof tags === "string" ? (
            <TagInfo value={[[<span key="only">{tags}</span>]]} />
          ) : (
            tags
          )}
        </div>
      </WithSpacing>
    )}
    {(text || readMore) && (
      <div className={styles.body}>
        <WithSpacing>
          <div className={styles.bodyContent}>
            {text && (
              <Truncate variant={textVariant} as="p" lines={textMaxLines}>
                {text}
              </Truncate>
            )}
            {readMore && (
              <Text variant="default">
                <MaybeLink
                  to={href}
                  // There's already a link for the card, this one has the same destination
                  tabIndex={-1}
                >
                  <GetText
                    aria-label={title}
                    id="read-more"
                    fallback="Read more"
                  />
                </MaybeLink>
              </Text>
            )}
            {children && <div className={styles.footer}>{children}</div>}
          </div>
        </WithSpacing>
      </div>
    )}
  </div>
);

export default CardContent;
